import type { UtilityContext } from '@change-corgi/core-react/utilityContext';
import type {
	FCM_MEMBERSHIP_ANNUAL_CONTRIBUTION,
	FCM_SUBSCRIPTION_CONFIGURATION,
	FcmConfigValueType,
} from '@change-corgi/fcm/configs';

import { type ContributionsPageData, ContributionsPageDataError, type ContributionsPageDataFromApi } from './types';

type SubscriptionConfiguration = NonNullable<FcmConfigValueType<typeof FCM_SUBSCRIPTION_CONFIGURATION>>;
type AnnualContributionsConfig = FcmConfigValueType<typeof FCM_MEMBERSHIP_ANNUAL_CONTRIBUTION>;

type BaseSubscriptionConfiguration = {
	amounts: readonly number[];
	minAmount: number;
	maxAmount: number;
};

const getSubscriptionConfig = (
	subscriptionConfiguration: BaseSubscriptionConfiguration,
	currencyCode: string,
	i18n: UtilityContext['i18n'],
) => {
	const { amountFromBaseUnits } = i18n;

	const amountChoices = subscriptionConfiguration.amounts.map((amount) => amountFromBaseUnits(amount, currencyCode));

	const maximumAmount = amountFromBaseUnits(subscriptionConfiguration.maxAmount, currencyCode);
	const minimumAmount = amountFromBaseUnits(subscriptionConfiguration.minAmount, currencyCode);

	return {
		amountChoices,
		minimumAmount,
		maximumAmount,
	};
};

export function processData(
	{ subscription, country }: ContributionsPageDataFromApi,
	subscriptionConfiguration: SubscriptionConfiguration,
	annualContributionsConfig: AnnualContributionsConfig | null,
	i18n: UtilityContext['i18n'],
): Omit<ContributionsPageData, 'featureConfigs'> {
	const { amountFromBaseUnits } = i18n;

	const amount = amountFromBaseUnits(subscription.amount, subscription.currencyCode);

	let amountConfiguration: BaseSubscriptionConfiguration = subscriptionConfiguration;
	if (subscription.interval === 'YEAR') {
		if (annualContributionsConfig?.enabled !== true) {
			throw new ContributionsPageDataError('contributions_page_data_not_found');
		}
		amountConfiguration = annualContributionsConfig;
	}

	return {
		subscription: {
			...subscription,
			amount,
			unsavedAmount: amount,
			config: getSubscriptionConfig(amountConfiguration, subscription.currencyCode, i18n),
		},
		countryCode: country?.countryCode ?? 'US',
		currency: country?.currency?.code || 'USD',
		currencyExchangeRate: country?.currency?.exchangeRate,
	};
}
